import React, { useState, useEffect } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  deleteIssueReason,
  reactivateIssueReason,
} from "../../services/reasonService";
import useFetchPaginatedIssueReasons from "../../hooks/useFetchPaginatedReasons";
import useFetchAllClassifications from "../../hooks/useFetchAllClassifications";
import useDebounce from "../../hooks/useDebounce";
import { getFormattedDateTime } from "../../services/utils";
import {
  IconDeleteBlueSvg,
  IconEditBase,
  IconEyeBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { useToast } from "../../services/utils";
import Loader from "../../components/Molecules/Loader/Loader";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import ButtonComponent from "../../components/Button.Component";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { getConfig } from "../../config/runtimeConfig";

const REASON_FILTER_CONFIG = {
  DEFAULT_STATE: {
    reason_name: "",
    filter_ids: "",
    status_filter: "Active",
  },
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["reason_name", "filter_ids", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const ReasonList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  const canViewReason = hasPermission("reason", "view");
  const canAddReason = hasPermission("reason", "add");
  const canEditReason = hasPermission("reason", "edit");
  const canDeleteReason = hasPermission("reason", "delete");

  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [reasonToDelete, setReasonToDelete] = useState<any>(null);

  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = { ...REASON_FILTER_CONFIG.DEFAULT_STATE };
    REASON_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) initialState[field] = urlValue;
    });
    return initialState;
  });

  const {
    reason_name: effectiveName = "",
    filter_ids: effectiveFilterIds = "",
    status_filter: effectiveStatusFilter = "Active",
  } = searchFilterData || {};

  const debouncedName = useDebounce(effectiveName);
  const effectiveNameSearch =
    debouncedName.trim().length >= 3 ? debouncedName : "";

  // Convert status_filter to isActive boolean
  const getIsActiveValue = (statusFilter: string): boolean | undefined => {
    if (statusFilter === "Active") return true;
    if (statusFilter === "Inactive") return false;
    return undefined; // "All" or empty - no filter
  };

  const [fetchedReasons, refetchReasons] = useFetchPaginatedIssueReasons({
    setIsLoading,
    page,
    pSize,
    searchTerm: effectiveNameSearch,
    filterIds: effectiveFilterIds,
    isActive: getIsActiveValue(effectiveStatusFilter),
  });

  // Fetch all classifications for lookup
  const [allClassifications] = useFetchAllClassifications({});

  // Helper function to get classification name by ID
  const getClassificationNameById = (
    classificationId: number | undefined
  ): string => {
    if (!classificationId || !allClassifications?.length) return "";
    const classification = allClassifications.find(
      (c: any) => c.id === classificationId
    );
    return classification?.issue_classification_name || "";
  };

  useEffect(() => {
    const newParams = new URLSearchParams();
    REASON_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        newParams.set(field, String(value));
      }
    });
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  useEffect(() => {
    const urlFilterState: any = { ...REASON_FILTER_CONFIG.DEFAULT_STATE };
    REASON_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) urlFilterState[field] = urlValue;
    });
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const norm = (v: any) =>
        v === null || v === undefined || v === "" ? "" : v;
      return norm(urlFilterState[key]) !== norm(searchFilterData[key]);
    });
    if (hasFilterChanges) setSearchFilterData(urlFilterState);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [effectiveNameSearch, effectiveFilterIds]);

  const handelClickEvent = () => navigate("/reason/add");

  const handleDeleteReason = (reason: any) => {
    setReasonToDelete(reason);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!reasonToDelete) return;
    try {
      setIsLoading(true);
      await deleteIssueReason(reasonToDelete.id);
      showToast("Reason deleted successfully", "success");
      setDeleteConfirmOpen(false);
      setReasonToDelete(null);
      refetchReasons();
    } catch (error: any) {
      showToast(error?.message || "Failed to delete reason", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivate = async (reason: any) => {
    try {
      setIsLoading(true);
      await reactivateIssueReason(reason.id);
      showToast("Reason reactivated successfully", "success");
      refetchReasons();
    } catch (error: any) {
      console.error("Error reactivating reason:", error);
      showToast(
        error?.response?.data?.detail || "Failed to reactivate reason",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: "ID",
      minWidth: 80,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => <span>{params.id}</span>,
    },
    {
      field: "reason_name",
      headerName: "Reason Name",
      minWidth: 200,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip value={params?.value} />
      ),
    },
    {
      field: "issue_classification_id",
      headerName: "Classification",
      minWidth: 180,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => {
        // Lookup classification name from the fetched classifications list
        const classificationName = getClassificationNameById(
          params?.row?.issue_classification_id
        );
        return <LimitChractersWithTooltip value={classificationName} />;
      },
    },
    {
      field: "updated_on",
      headerName: "Last Modified",
      minWidth: 180,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => getFormattedDateTime(params.value),
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) =>
        params.value ? (
          <Tooltip title="Active" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Inactive" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ),
    },
    {
      field: "action",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      width: 170,
      sortable: false,
      renderCell: (params: any) => {
        const isActive = params.row.is_active === true;
        return (
          <>
            {isActive && (
              <>
                {canViewReason && (
                  <Tooltip title="View Reason" placement="top" arrow>
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => navigate(`/reason/view/${params.row.id}`)}
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canEditReason && (
                  <Tooltip title="Edit Reason" placement="top" arrow>
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => navigate(`/reason/edit/${params.row.id}`)}
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canDeleteReason && (
                  <Tooltip title="Delete Reason" placement="top" arrow>
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => handleDeleteReason(params.row)}
                    >
                      <IconDeleteBlueSvg />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            {!isActive && canEditReason && (
              <Tooltip title="Reactivate Reason" placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleReactivate(params.row)}
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete Reason"
        dailogDescription={`Are you sure you want to delete reason "${reasonToDelete?.reason_name}"?`}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={["reason_name", "status_filter"]}
              primaryFields={["reason_name", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active",
              }}
            />
          </Grid>
          {canAddReason && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <ButtonComponent
                handelClickEvent={handelClickEvent}
                className="btn-orange"
              >
                <>
                  <AddSharpIcon sx={{ marginRight: "4px" }} />
                  Reason
                </>
              </ButtonComponent>
            </Grid>
          )}
        </Grid>
      </Box>
      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={
            Array.isArray(fetchedReasons?.items) ? fetchedReasons.items : []
          }
          loading={isLoading}
          dataListTitle={"Reason List"}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fetchedReasons?.total || 0}
          paginationModel={{ page: page - 1, pageSize: pSize }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={368}
        />
      </FlexBetween>
    </Box>
  );
};

export default ReasonList;
